import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { getBaseParams } from 'src/comps/Page/selectors'
import Button from 'src/comps/Atom/Buttons/Button'
import PaymentMethodsLogos from 'src/comps/PaymentMethodsLogos'
import { validateMerchantApplePay } from './requests'
import {
  ApplePayProps,
  MerchantSession,
  ApplePayValidateMerchantEvent,
  ApplePayPaymentAuthorizedEvent,
} from './types'
import { ALTERNATIVE_PAYMENT_METHOD } from 'src/comps/PaymentForm/constants'
import { SUPPORTED_NETWORKS, MERCHANT_LABEL, MERCHANT_CAPABILITIES } from './constants'

const ApplePay: React.FC<ApplePayProps> = ({
  onSuccess,
  onError,
  purchaseId,
  payNow: payNowNumber = 0,
}) => {
  const { market, currency } = useSelector(getBaseParams)
  const [isProcessing, setIsProcessing] = useState(false)
  const [domain, setDomain] = useState('')
  const payNow = payNowNumber?.toString()

  const handleOnError = (error: string): void => {
    onError(`ApplePay: ${error}`)
  }

  useEffect(() => {
    if (!domain) setDomain(window.location.hostname)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleMerchantValidation = async ({
    session,
    validateEvent,
  }: {
    session: ApplePaySession
    validateEvent: ApplePayValidateMerchantEvent
  }) => {
    try {
      const response = await validateMerchantApplePay({
        purchaseId,
        validationUrl: validateEvent.validationURL,
        domain,
      })

      if (response.status !== 200) {
        throw new Error(`Error fetching merchant session: status ${response.status}`)
      }

      // eslint-disable-next-line camelcase
      const merchantSession: MerchantSession = response.data.validation_response
      session.completeMerchantValidation(merchantSession)
    } catch (error) {
      session.abort()
      if (error instanceof Error) {
        handleOnError(`Merchant validation failed: ${error.message}`)
      } else {
        handleOnError('Merchant validation failed: Unknown error')
      }
    }
  }

  const handlePaymentAuthorized = async ({
    session,
    paymentEvent,
  }: {
    session: ApplePaySession
    paymentEvent: ApplePayPaymentAuthorizedEvent
  }) => {
    try {
      const { ephemeralPublicKey: ephemeralKey, publicKeyHash: publicKey } =
        paymentEvent.payment.token.paymentData.header
      const { data: paymentData } = paymentEvent.payment.token.paymentData

      session.completePayment({ status: ApplePaySession.STATUS_SUCCESS })
      onSuccess(ALTERNATIVE_PAYMENT_METHOD, {
        id: 'applePay',
        purchaseId,
        ephemeralKey,
        publicKey,
        paymentData,
      })
    } catch (error) {
      session.completePayment({ status: ApplePaySession.STATUS_FAILURE })
      handleOnError('Payment authorization failed')
    }
  }

  const handleApplePayClick = (event: React.MouseEvent<HTMLButtonElement> | undefined) => {
    event?.preventDefault()

    const formattedPayNow = Number(payNow).toFixed(2)

    const paymentDetails = {
      countryCode: market.toUpperCase(),
      currencyCode: currency,
      merchantCapabilities: MERCHANT_CAPABILITIES,
      supportedNetworks: SUPPORTED_NETWORKS,
      total: { ...MERCHANT_LABEL, amount: formattedPayNow },
    }

    try {
      if (payNow <= '0') {
        handleOnError('Payment value must be greater than 0')
        return
      }
      if (isProcessing || !domain) return
      setIsProcessing(true)

      if (ApplePaySession?.canMakePayments()) {
        const session = new ApplePaySession(3, paymentDetails)

        session.onvalidatemerchant = validateEvent => {
          handleMerchantValidation({ session, validateEvent })
        }

        session.onpaymentauthorized = paymentEvent => {
          handlePaymentAuthorized({ session, paymentEvent })
        }

        session.oncancel = (e: ApplePayJS.Event) => {
          const reason =
            (e as { sessionError?: { code: string } })?.sessionError?.code ?? 'No reason provided'
          handleOnError(`User cancelled session with reason: ${reason}`)
        }

        session.begin()
      } else {
        handleOnError('User browser unable to initialize session')
      }
    } catch (error) {
      handleOnError('Error initializing session')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <Button
      data-testId="apple-pay-button"
      onClick={handleApplePayClick}
      disabled={isProcessing}
      size="m"
    >
      <FormattedMessage id="global.pay-with" />
      <PaymentMethodsLogos applePay width={60} height={46} />
    </Button>
  )
}

export default ApplePay
